<template>
  <div class="customers py-3">
    <v-card class="mx-auto">
      <!-- TOOLBAR -->
      <v-toolbar color="#E21A22" dark>
        <v-toolbar-title>Customers</v-toolbar-title>
      </v-toolbar>

      <!-- SEARCH -->
      <v-card-text>
        <v-text-field
          v-model="search"
          label="Search by business name, address or contact"
          prepend-inner-icon="mdi-magnify"
          clearable
          outlined
          dense
          @input="searchCustomers"
        />
      </v-card-text>

      <!-- EMPTY -->
      <v-list v-if="!customers.length && !loading">
        <v-list-item>
          <v-list-item-content>
            <v-list-item-title class="text-center grey--text">
              No customers found
            </v-list-item-title>
          </v-list-item-content>
        </v-list-item>
      </v-list>

      <!-- LIST -->
      <v-list two-line v-if="customers.length">
        <v-divider />

        <v-list-item v-for="cust in customers" :key="cust.id">
          <v-list-item-content>
            <v-list-item-title>
              <strong>{{ cust.businessName }}</strong>
            </v-list-item-title>
            <v-list-item-subtitle v-if="cust.temporarily_suspended">
              <v-chip
                x-small
                color="orange"
                text-color="white"
              >Suspended</v-chip>
            </v-list-item-subtitle>
            <v-list-item-subtitle>
              {{ cust.serviceAddressStreet }} {{ cust.serviceAddressNumber }},
              <span v-if="cust.suite_unit">Suite/Unit {{ cust.suite_unit }},</span>
              {{ cust.city }}
            </v-list-item-subtitle>
            <v-list-item-subtitle>
              {{ cust.contactPersonFirstName }}
              {{ cust.contactPersonLastName }} - {{ cust.phoneNumber }}
            </v-list-item-subtitle>
          </v-list-item-content>

          <v-list-item-action class="d-flex flex-row align-center">
            <v-btn icon color="primary" @click="openDetails(cust)">
              <v-icon>mdi-eye</v-icon>
            </v-btn>
            <v-btn icon color="success" @click="openManualRoute(cust)">
              <v-icon>mdi-map-marker-plus</v-icon>
            </v-btn>
            <v-btn v-if="$store.getters.driver_type === 'Manager'" icon color="orange" @click="openEdit(cust)">
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </v-list-item-action>
        </v-list-item>

        <v-divider />
      </v-list>
    </v-card>

    <!-- DETAILS DIALOG -->
    <v-dialog v-model="dialogDetails" fullscreen>
      <v-card>
        <v-toolbar color="#1B6BAA" dark>
          <v-btn icon @click="dialogDetails = false">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-toolbar-title>{{ selected.businessName }}</v-toolbar-title>
          <v-spacer />
          <v-btn v-if="$store.getters.driver_type === 'Manager'" text :disabled="detailsLoading" @click="openEdit(selected)">Edit</v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <v-progress-linear
            v-if="detailsLoading"
            indeterminate
            color="primary"
            class="mb-4"
          />

          <v-card outlined class="mb-3">
            <v-card-subtitle class="font-weight-bold"
              >General Information</v-card-subtitle
            >
            <v-card-text>
              <p>
                <strong>Account Number:</strong> {{ selected.accountNumber }}
              </p>
              <p><strong>Business Name:</strong> {{ selected.businessName }}</p>
              <p>
                <strong>Status:</strong>
                {{ selected.active === "Yes" ? "Active" : "Inactive" }}
              </p>
              <p>
                <strong>Temporarily Suspended:</strong>
                {{ Number(selected.temporarily_suspended) ? "Yes" : "No" }}
              </p>
            </v-card-text>
          </v-card>

          <v-card outlined class="mb-3">
            <v-card-subtitle class="font-weight-bold"
              >Service Details</v-card-subtitle
            >
            <v-card-text>
              <CustomerVisits v-if="selected.id && $store.getters.driver_type === 'Manager'" :key="selected.id" :customer-id="selected.id" />
              <p>
                <strong>Frequency:</strong>
                {{ frequencyLabel(selected.frequency) }}
              </p>

              <div class="mb-3">
                <strong>Containers at location:</strong>
                <div
                  v-if="selected.containersAtLocation && selected.containersAtLocation.length"
                  class="mt-2"
                >
                  <v-chip
                    v-for="(container, index) in selected.containersAtLocation"
                    :key="'container-' + (container.id || index)"
                    small
                    class="mr-1 mb-1"
                    color="green"
                    text-color="white"
                  >
                    {{ container.container }}
                  </v-chip>
                </div>
                <p v-else class="mt-2 mb-0">N/A</p>
              </div>

              <div class="mb-3">
                <strong>Hours of operation:</strong>
                <div
                  v-if="selected.daysAndHoursOfOperation && selected.daysAndHoursOfOperation.length"
                  class="mt-2"
                >
                  <v-chip
                    v-for="(hour, index) in selected.daysAndHoursOfOperation"
                    :key="'hour-' + (hour.id || index)"
                    small
                    class="mr-1 mb-1"
                    outlined
                  >
                    {{ hour.name }}
                  </v-chip>
                </div>
                <p v-else class="mt-2 mb-0">N/A</p>
              </div>

              <div>
                <strong>Routes assigned:</strong>
                <div
                  v-if="selected.routes && selected.routes.length"
                  class="mt-2"
                >
                  <v-chip
                    v-for="(route, index) in selected.routes"
                    :key="'route-' + (route.id || index)"
                    small
                    class="mr-1 mb-1"
                    color="primary"
                    text-color="white"
                  >
                    {{ route.route }}
                  </v-chip>
                </div>
                <p v-else class="mt-2 mb-0">N/A</p>
              </div>
            </v-card-text>
          </v-card>

          <v-card outlined class="mb-3">
            <v-card-subtitle class="font-weight-bold">Address</v-card-subtitle>
            <v-card-text>
              <p>
                <strong>Street:</strong> {{ selected.serviceAddressStreet }}
              </p>
              <p>
                <strong>Number:</strong> {{ selected.serviceAddressNumber }}
              </p>
              <p><strong>City:</strong> {{ selected.city }}</p>
              <p v-if="selected.suite_unit"><strong>Suite/Unit:</strong> {{ selected.suite_unit }}</p>
              <p v-if="selected.serviceAddressState"><strong>State:</strong> {{ selected.serviceAddressState }}</p>
              <p><strong>Country:</strong> {{ selected.country }}</p>
              <p><strong>Zip Code:</strong> {{ selected.zipCode }}</p>
              <p>
                <strong>Billing Address:</strong>
                {{ selected.billingAddress || "N/A" }}
              </p>
            </v-card-text>
          </v-card>

          <v-card outlined class="mb-3">
            <v-card-subtitle class="font-weight-bold"
              >Contact Information</v-card-subtitle
            >
            <v-card-text>
              <p>
                <strong>Contact Person:</strong>
                {{ selected.contactPersonFirstName }}
                {{ selected.contactPersonLastName }}
              </p>
              <p>
                <strong>Title:</strong>
                {{ selected.titleOfContactPerson || "N/A" }}
              </p>
              <p><strong>Email:</strong> {{ selected.email || "N/A" }}</p>
              <p><strong>Phone:</strong> {{ selected.phoneNumber || "N/A" }}</p>
              <p><strong>Fax:</strong> {{ selected.faxNumber || "N/A" }}</p>
              <p>
                <strong>Alt Email:</strong>
                {{ selected.alternativeEmail || "N/A" }}
              </p>
              <p>
                <strong>Alt Phone:</strong>
                {{ selected.alternativePhoneNumber || "N/A" }}
              </p>
            </v-card-text>
          </v-card>

          <v-card outlined class="mb-3" v-if="selected.notes">
            <v-card-subtitle class="font-weight-bold">Permanent Customer Notes</v-card-subtitle>
            <v-card-text>
              <p>{{ selected.notes }}</p>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- MANUAL ROUTE DIALOG -->
    <v-dialog
      v-model="dialogManualRoute"
      fullscreen
      hide-overlay
      transition="dialog-bottom-transition"
    >
      <v-card>
        <v-toolbar color="success" dark>
          <v-btn icon @click="dialogManualRoute = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
          <v-toolbar-title>Add Manual Route</v-toolbar-title>
          <v-spacer />
          <v-btn text :loading="manualRouteSaving" @click="saveManualRoute">
            SAVE
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <v-card outlined class="mb-4">
            <v-card-subtitle class="font-weight-bold green--text">
              {{ selectedManualCustomer.businessName }}
            </v-card-subtitle>
            <v-card-text>
              <p class="mb-1">
                <strong>Account Number:</strong>
                {{ selectedManualCustomer.accountNumber || "N/A" }}
              </p>
              <p class="mb-1">
                <strong>Address:</strong>
                {{ selectedManualCustomer.serviceAddressStreet }}
                {{ selectedManualCustomer.serviceAddressNumber }},
                <span v-if="selectedManualCustomer.suite_unit">Suite/Unit {{ selectedManualCustomer.suite_unit }},</span>
                {{ selectedManualCustomer.city }}
              </p>
              <p class="mb-0">
                <strong>Phone:</strong>
                {{ selectedManualCustomer.phoneNumber || "N/A" }}
              </p>
            </v-card-text>
          </v-card>

          <v-card outlined class="mb-4">
            <v-card-subtitle class="font-weight-bold green--text">
              Route Details
            </v-card-subtitle>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <v-select
                    v-model="manualRoute.driver_id"
                    :items="driversList"
                    item-text="name"
                    item-value="id"
                    label="Driver"
                    outlined
                    dense
                    :loading="driversLoading"
                    :disabled="driversLoading || !driversList.length"
                    :hint="driversHint"
                    persistent-hint
                  >
                    <template v-slot:selection="{ item }">
                      <span v-if="item && item.letter">
                        {{ item.letter }} - {{ item.name }}
                      </span>
                      <span v-else-if="item">
                        {{ item.name }}
                      </span>
                    </template>
                    <template v-slot:item="{ item }">
                      <v-list-item-content>
                        <v-list-item-title>
                          <span v-if="item.letter">
                            {{ item.letter }} - {{ item.name }}
                          </span>
                          <span v-else>
                            {{ item.name }}
                          </span>
                        </v-list-item-title>
                      </v-list-item-content>
                    </template>
                  </v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="manualRoute.pickup_date"
                    label="Pick Up Date"
                    type="date"
                    outlined
                    dense
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model.number="manualRoute.order"
                    label="Order"
                    type="number"
                    outlined
                    dense
                    hint="Position in the route"
                    persistent-hint
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="manualRoute.contact_person_at_clinic"
                    label="Contact Person at Clinic"
                    outlined
                    dense
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-card outlined>
            <v-card-subtitle class="font-weight-bold orange--text">
              Notes
            </v-card-subtitle>
            <v-card-text>
              <v-row>
                <v-col cols="12" md="6">
                  <v-textarea
                    v-model="manualRoute.notes"
                    label="Notes"
                    outlined
                    rows="3"
                  />
                </v-col>
                <v-col cols="12" md="6">
                  <v-textarea
                    v-model="manualRoute.notes_to_driver"
                    label="Notes to Driver"
                    outlined
                    rows="3"
                  />
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- EDIT DIALOG -->
    <v-dialog v-if="$store.getters.driver_type === 'Manager'" v-model="dialogEdit" fullscreen>
      <v-card>
        <v-toolbar color="#FB8C00" dark>
          <v-btn icon :disabled="customerSaving" @click="dialogEdit = false">
            <v-icon>mdi-chevron-left</v-icon>
          </v-btn>
          <v-toolbar-title>Edit Customer</v-toolbar-title>
          <v-spacer />
          <v-btn text :disabled="editLoading || !editReady || customerSaving" :loading="customerSaving" @click="saveCustomer">SAVE</v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <v-progress-linear v-if="editLoading" indeterminate />
          <v-alert v-if="editError" type="error" dense>{{ editError }}</v-alert>
          <v-card outlined class="mb-3">
            <v-card-subtitle class="font-weight-bold"
              >General Information</v-card-subtitle
            >
            <v-card-text>
              <v-text-field
                v-model="form.businessName"
                label="Business Name"
                outlined
                dense
              />
              <v-text-field
                v-model="form.accountNumber"
                label="Account Number"
                outlined
                dense
              />
              <v-switch v-model="form.active" label="Active" />
              <v-switch
                v-model="form.temporarily_suspended"
                label="Temporarily Suspended"
              />
            </v-card-text>
          </v-card>

          <v-card outlined class="mb-3">
            <v-card-subtitle class="font-weight-bold"
              >Service Address</v-card-subtitle
            >
            <v-card-text>
              <v-text-field
                v-model="form.serviceAddressStreet"
                label="Street"
                outlined
                dense
              />
              <v-text-field
                v-model="form.serviceAddressNumber"
                label="Number"
                outlined
                dense
              />
              <v-text-field v-model="form.city" label="City" outlined dense />
              <v-text-field
                v-model="form.county"
                label="County"
                outlined
                dense
              />
              <v-text-field v-model="form.serviceAddressState" label="State" outlined dense />
              <v-text-field v-model="form.suite_unit" label="Suite / Unit" outlined dense />
              <v-text-field
                v-model="form.zipCode"
                label="Zip Code"
                outlined
                dense
              />
              <v-text-field
                v-model="form.billingAddress"
                label="Billing Address"
                outlined
                dense
              />
            </v-card-text>
          </v-card>

          <v-card outlined class="mb-3">
            <v-card-subtitle class="font-weight-bold"
              >Contact Information</v-card-subtitle
            >
            <v-card-text>
              <v-text-field
                v-model="form.contactPersonFirstName"
                label="First Name"
                outlined
                dense
              />
              <v-text-field
                v-model="form.contactPersonLastName"
                label="Last Name"
                outlined
                dense
              />
              <v-text-field
                v-model="form.titleOfContactPerson"
                label="Title"
                outlined
                dense
              />
              <v-text-field v-model="form.email" label="Email" outlined dense />
              <v-text-field
                v-model="form.phoneNumber"
                label="Phone Number"
                outlined
                dense
              />
              <v-text-field
                v-model="form.faxNumber"
                label="Fax Number"
                outlined
                dense
              />
              <v-text-field
                v-model="form.alternativeEmail"
                label="Alternative Email"
                outlined
                dense
              />
              <v-text-field
                v-model="form.alternativePhoneNumber"
                label="Alternative Phone"
                outlined
                dense
              />
            </v-card-text>
          </v-card>

          <v-card outlined class="mb-3">
            <v-card-subtitle class="font-weight-bold"
              >Additional Information</v-card-subtitle
            >
            <v-card-text>
              <v-textarea
                v-model="form.notes"
                label="Permanent customer notes"
                outlined
                rows="3"
              />
            </v-card-text>
          </v-card>
          <v-card outlined class="mb-3">
            <v-card-subtitle class="font-weight-bold">Operational Settings</v-card-subtitle>
            <v-card-text>
              <p>Permanent assignments apply to future scheduling. Already-generated visits retain their current assignments.</p>
              <v-autocomplete v-model="form.route_ids" :items="editOptions.routes" item-value="id" item-text="route" label="Permanent routes" multiple chips outlined :disabled="editLoading || customerSaving" />
              <v-select v-model="form.frequency_id" :items="editOptions.frequencies" item-value="id" item-text="frequency" label="Pickup frequency" outlined :disabled="editLoading || customerSaving" />
              <v-autocomplete v-model="form.hour_ids" :items="editOptions.hours" item-value="id" item-text="name" label="Operating days and hours" multiple chips outlined :disabled="editLoading || customerSaving" />
              <p>Unselected days are closed. Assigned routes must fall on a selected operating day.</p>
              <v-autocomplete v-model="form.container_ids" :items="editOptions.containers" item-value="id" item-text="container" label="Containers at location" multiple chips outlined :disabled="editLoading || customerSaving" />
            </v-card-text>
          </v-card>
          <v-card outlined class="mb-3">
            <v-card-subtitle class="font-weight-bold">Temporary Visit Notes</v-card-subtitle>
            <v-card-text>
              <v-select v-model="selectedNoteVisit" :items="editOptions.visits" item-value="id" item-text="label" label="Select scheduled visit" clearable outlined :disabled="editLoading || customerSaving" @change="selectNoteVisit" />
              <p v-if="!editOptions.visits.length">No current scheduled visits are available.</p>
              <v-textarea v-if="selectedNoteVisit" v-model="visitNotes" label="Instructions for this visit only" outlined :disabled="customerSaving" />
            </v-card-text>
          </v-card>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CustomerVisits from "@/components/CustomerVisits.vue";

export default {
  components: { CustomerVisits },
  data: () => ({
    search: "",
    customers: [],
    loading: false,
    detailsLoading: false,
    dialogDetails: false,
    dialogEdit: false,
    editLoading: false,
    editReady: false,
    customerSaving: false,
    editError: "",
    editOptions: { routes: [], frequencies: [], hours: [], containers: [], visits: [] },
    selectedNoteVisit: null,
    visitNotes: "",
    dialogManualRoute: false,
    selected: {},
    selectedManualCustomer: {},
    form: {},
    driversList: [],
    driversLoading: false,
    manualRouteSaving: false,
    manualRoute: {
      customer_id: null,
      driver_id: null,
      pickup_date: "",
      contact_person_at_clinic: "",
      notes: "",
      notes_to_driver: "",
      order: 0,
    },
    searchTimeout: null,
  }),

  computed: {
    driversHint() {
      if (this.driversLoading) {
        return "Loading drivers...";
      }

      if (this.driversList.length) {
        return `${this.driversList.length} drivers available`;
      }

      return "No drivers available";
    },
  },

  methods: {
    searchCustomers() {
      var este = this;

      // Validación de búsqueda mínima
      if (!este.search || este.search.length < 2) {
        este.customers = [];
        return;
      }

      // Debounce para no spamear el API
      clearTimeout(este.searchTimeout);
      este.searchTimeout = setTimeout(() => {
        este.loading = true;

        este.$http
          .post(este.$api + "customers/search", {
            driver_id: este.$store.getters.driver_id,
            token: este.$store.getters.token,
            query: este.search,
          })
          .then(function (response) {
            if (response.status == 200 && response.data.status == "ok") {
              este.customers = response.data.data;
            } else {
              var mensaje = {
                color: "red",
                timeout: 5000,
                message: response.data.msg,
              };
              este.$store.commit("msgMuestra", mensaje);
              este.customers = [];
            }
          })
          .catch(function (err) {
            este.customers = [];
            var mensaje = {
              color: "red",
              timeout: 5000,
              message: "There was an error. Please try again.",
            };
            este.$store.commit("msgMuestra", mensaje);
            window.console.log(err);
          })
          .finally(() => {
            este.loading = false;
          });
      }, 350);
    },

    frequencyLabel(frequency) {
      if (!frequency) {
        return "N/A";
      }

      if (typeof frequency === "string") {
        return frequency;
      }

      return frequency.frequency || frequency.name || "N/A";
    },

    openDetails(cust) {
      var este = this;

      este.selected = { ...cust };
      este.dialogDetails = true;
      este.detailsLoading = true;

      este.$http
        .post(este.$api + "customers/detail", {
          driver_id: este.$store.getters.driver_id,
          token: este.$store.getters.token,
          customer_id: cust.id,
        })
        .then(function (response) {
          if (response.status == 200 && response.data.status == "ok") {
            este.selected = response.data.data;
          } else {
            var mensaje = {
              color: "red",
              timeout: 5000,
              message: response.data.msg || "Unable to load customer details",
            };
            este.$store.commit("msgMuestra", mensaje);

            if ("error" in response.data && response.data.error == "logout") {
              este.$router.push("/logout");
            }
          }
        })
        .catch(function (err) {
          var mensaje = {
            color: "red",
            timeout: 5000,
            message: "There was an error loading customer details.",
          };
          este.$store.commit("msgMuestra", mensaje);
          window.console.log(err);
        })
        .finally(() => {
          este.detailsLoading = false;
        });
    },

    async openEdit(cust) {
      if (this.$store.getters.driver_type !== "Manager") return;
      this.form = {};
      this.dialogEdit = true;
      this.editLoading = true;
      this.editReady = false;
      this.editError = "";
      this.selectedNoteVisit = null;
      this.visitNotes = "";
      try {
        const credentials = { driver_id: this.$store.getters.driver_id, token: this.$store.getters.token, customer_id: cust.id };
        const [detail, options] = await Promise.all([
          this.$http.post(this.$api + "customers/detail", credentials),
          this.$http.post(this.$api + "customerEditOptions", credentials),
        ]);
        if (detail.data.status !== "ok") throw new Error(detail.data.msg);
        if (options.data.status !== "ok") throw new Error(options.data.msg);
        const customer = detail.data.data;
        this.editOptions = options.data.data;
        Object.keys(this.editOptions).forEach(key => {
          this.editOptions[key] = this.editOptions[key].map(item => ({ ...item, id: Number(item.id) }));
        });
        this.form = {
          ...customer,
          active: customer.active === "Yes",
          temporarily_suspended: Boolean(Number(customer.temporarily_suspended)),
          frequency_id: customer.frequency_id ? Number(customer.frequency_id) : null,
          route_ids: (customer.routes || []).map(item => Number(item.id)),
          hour_ids: (customer.daysAndHoursOfOperation || []).map(item => Number(item.id)),
          container_ids: (customer.containersAtLocation || []).map(item => Number(item.id)),
        };
        this.editReady = true;
      } catch (error) {
        this.editError = (error.response && error.response.data && (error.response.data.msg || error.response.data.message)) || error.message;
      } finally {
        this.editLoading = false;
      }
    },

    selectNoteVisit(id) {
      const visit = this.editOptions.visits.find(item => item.id === id);
      this.visitNotes = visit ? visit.notes || "" : "";
    },

    openManualRoute(cust) {
      var contactPerson = [
        cust.contactPersonFirstName,
        cust.contactPersonLastName,
      ]
        .filter(Boolean)
        .join(" ");

      this.selectedManualCustomer = { ...cust };
      this.manualRoute = {
        customer_id: cust.id,
        driver_id: null,
        pickup_date: this.$moment().format("YYYY-MM-DD"),
        contact_person_at_clinic: contactPerson,
        notes: "",
        notes_to_driver: "",
        order: 0,
      };
      this.dialogManualRoute = true;

      if (!this.driversList.length) {
        this.loadDriversList();
      }
    },

    loadDriversList() {
      var este = this;

      if (este.driversLoading) {
        return;
      }

      este.driversLoading = true;
      este.$http
        .post(este.$api + "allDrivers", { manual_route: true })
        .then(function (response) {
          if (response.status == 200 && response.data.status == "ok") {
            este.driversList = response.data.data.map((driver) => ({
              id: parseInt(driver.id),
              name: driver.name,
              letter: driver.letter,
            }));
          } else {
            var mensaje = {
              color: "red",
              timeout: 5000,
              message: response.data.msg || "Unable to load drivers",
            };
            este.$store.commit("msgMuestra", mensaje);
          }
        })
        .catch(function (err) {
          var mensaje = {
            color: "red",
            timeout: 5000,
            message: "There was an error loading drivers.",
          };
          este.$store.commit("msgMuestra", mensaje);
          window.console.log(err);
        })
        .finally(() => {
          este.driversLoading = false;
        });
    },

    saveManualRoute() {
      var este = this;
      var mensaje;

      if (!este.manualRoute.customer_id) {
        mensaje = {
          color: "red",
          timeout: 5000,
          message: "Please select a customer",
        };
        este.$store.commit("msgMuestra", mensaje);
        return;
      }

      if (!este.manualRoute.driver_id) {
        mensaje = {
          color: "red",
          timeout: 5000,
          message: "Please select a driver",
        };
        este.$store.commit("msgMuestra", mensaje);
        return;
      }

      var routeDriverId = parseInt(este.manualRoute.driver_id);
      if (isNaN(routeDriverId)) {
        mensaje = {
          color: "red",
          timeout: 5000,
          message: "Invalid driver selection",
        };
        este.$store.commit("msgMuestra", mensaje);
        return;
      }

      if (!este.manualRoute.pickup_date) {
        mensaje = {
          color: "red",
          timeout: 5000,
          message: "Please select a pickup date",
        };
        este.$store.commit("msgMuestra", mensaje);
        return;
      }

      if (
        este.manualRoute.order === null ||
        este.manualRoute.order === undefined ||
        este.manualRoute.order < 0
      ) {
        mensaje = {
          color: "red",
          timeout: 5000,
          message: "Please enter a valid order number",
        };
        este.$store.commit("msgMuestra", mensaje);
        return;
      }

      este.manualRouteSaving = true;
      este.$http
        .post(este.$api + "addManualRoute", {
          driver_id: este.$store.getters.driver_id,
          token: este.$store.getters.token,
          customer_id: este.manualRoute.customer_id,
          route_driver_id: routeDriverId,
          pickup_date: este.manualRoute.pickup_date,
          order: este.manualRoute.order,
          notes: este.manualRoute.notes,
          notes_to_driver: este.manualRoute.notes_to_driver,
          contact_person_at_clinic: este.manualRoute.contact_person_at_clinic,
        })
        .then(function (response) {
          if (response.status == 200 && response.data.status == "ok") {
            mensaje = {
              color: "success",
              timeout: 5000,
              message: response.data.msg || "Route has been added successfully.",
            };
            este.$store.commit("msgMuestra", mensaje);
            este.dialogManualRoute = false;
          } else {
            mensaje = {
              color: "red",
              timeout: 5000,
              message: response.data.msg || "There was an error",
            };
            este.$store.commit("msgMuestra", mensaje);

            if ("error" in response.data && response.data.error == "logout") {
              este.$router.push("/logout");
            }
          }
        })
        .catch(function (err) {
          mensaje = {
            color: "red",
            timeout: 5000,
            message:
              err.response && err.response.data && err.response.data.msg
                ? err.response.data.msg
                : "There was an error. Please try again.",
          };
          este.$store.commit("msgMuestra", mensaje);
          window.console.log(err);
        })
        .finally(() => {
          este.manualRouteSaving = false;
        });
    },

    saveCustomer() {
      if (!this.editReady || this.customerSaving || this.$store.getters.driver_type !== "Manager") return;
      var este = this;
      var mensaje;
      este.customerSaving = true;
      este.editError = "";
      const customer = { ...este.form, active: este.form.active ? "Yes" : "No" };
      if (este.selectedNoteVisit) {
        customer.visit_id = este.selectedNoteVisit;
        customer.visit_notes = este.visitNotes;
      }

      este.$http
        .post(este.$api + "customers/update", {
          driver_id: este.$store.getters.driver_id,
          token: este.$store.getters.token,
          customer: customer,
        })
        .then(function (response) {
          if (response.status == 200 && response.data.status == "ok") {
            este.dialogEdit = false;

            mensaje = {
              color: "success",
              timeout: 5000,
              message: response.data.msg || "Customer updated",
            };
            este.$store.commit("msgMuestra", mensaje);

            este.searchCustomers();
            if (este.dialogDetails && este.selected.id === este.form.id) {
              este.openDetails({ id: este.form.id });
            }
          } else {
            mensaje = {
              color: "red",
              timeout: 5000,
              message: response.data.msg || "Update failed",
            };
            este.$store.commit("msgMuestra", mensaje);

            if ("error" in response.data && response.data.error == "logout") {
              este.$router.push("/logout");
            }
          }
        })
        .catch(function (err) {
          este.editError = (err.response && err.response.data && (err.response.data.msg || err.response.data.message)) || "There was an error. Please try again.";
          mensaje = {
            color: "red",
            timeout: 5000,
            message: este.editError,
          };
          este.$store.commit("msgMuestra", mensaje);
          window.console.log(err);
        })
        .finally(() => {
          este.customerSaving = false;
        });
    },
  },

  mounted: function () {
    this.$store.commit("title", "Customers");
    this.loadDriversList();
  },
};
</script>

<style scoped>
.customers {
  max-width: 1200px;
}
</style>
